import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { Card } from '../components/Card.tsx';
import {
  QuoteDocumentPanel,
  type IncomingDraft,
} from '../components/QuoteDocumentPanel.tsx';
import { QuoteSowChat, type SowCurrent } from '../components/QuoteSowChat.tsx';
import { QuoteDocumentView } from '../components/QuoteDocumentView.tsx';
import { Eye, X } from 'lucide-react';
import { Button } from '../components/Button.tsx';
import {
  CLIENT_ADDRESS_TOKEN,
  documentUsesToken,
  fillClientBlocks,
  splitDeposit,
  type QuoteDocument,
} from '@t3/shared';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';
import { EmailBody } from '../components/EmailBody.tsx';
import type { QuoteTemplateSummary } from '@t3/shared';

// One quote, end to end: write the scope, price it, send it, see what
// came back. This is the "separate place to do the quotes" — the ticket
// page shows the resulting scope read-only and nothing else.

interface TicketSummary {
  id: string;
  displayNumber: number;
  subject: string;
  status: string;
  organizationName: string;
  descriptionHtml: string;
  requesterName: string | null;
  customFields: Record<string, unknown>;
}

/** The client's request as plain text, to start the scoping chat with. */
function requestText(ticket: TicketSummary): string {
  if (!ticket.descriptionHtml.trim()) return '';
  const text = new DOMParser().parseFromString(ticket.descriptionHtml, 'text/html').body.textContent ?? '';
  const when = typeof ticket.customFields.time_frame === 'string' ? ticket.customFields.time_frame : '';
  return (`The client asked for: ${text.trim()}` + (when ? `
Needed: ${when}` : '')).slice(0, 4000);
}

export function QuoteEditorPage(): React.ReactElement {
  const { ticketId } = useParams<{ ticketId: string }>();
  const [ticket, setTicket] = useState<TicketSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A draft handed over from the chat, and a counter so applying the
  // same draft twice still loads it.
  const [incoming, setIncoming] = useState<IncomingDraft | null>(null);
  const [incomingSeq, setIncomingSeq] = useState(0);
  // What the quote says right now, reported up by the builder so each
  // chat turn revises the scope rather than redrafting it from memory.
  const [currentSow, setCurrentSow] = useState<SowCurrent | null>(null);
  // Preview shows the SAVED document through the same component the
  // client-facing page uses, so what is previewed cannot drift from what
  // gets sent.
  const [previewing, setPreviewing] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<QuoteDocument | null>(null);
  const [previewPlanId, setPreviewPlanId] = useState<string | null>(null);
  // Whether a document has been saved yet. A quote a client requested
  // from the portal starts without one, and can then start from a template.
  const [hasDocument, setHasDocument] = useState<boolean | null>(null);
  const [templates, setTemplates] = useState<QuoteTemplateSummary[]>([]);
  const [applying, setApplying] = useState<string | null>(null);
  const [templateError, setTemplateError] = useState<string | null>(null);
  // Bumped after a template is applied, so the builder reloads the new document.
  const [panelKey, setPanelKey] = useState(0);

  useDocumentTitle(ticket ? `Quote — ${ticket.subject}` : 'Quote');

  // NO setLoading(true) here, deliberately. This is also the after-save
  // refresh (`onSaved`), and the full-page "Loading the quote…" branch
  // below UNMOUNTS the editor — which blanked the whole document every
  // time it was saved. `loading` starts true, so the first load still
  // shows the spinner; every later refetch happens underneath a page
  // that stays exactly where it is.
  const load = useCallback(async () => {
    setError(null);
    try {
      const [data, doc] = await Promise.all([
        apiFetch<{ ticket: TicketSummary }>(`/api/tickets/${ticketId}`),
        apiFetch<{ document: QuoteDocument | null }>(`/api/tickets/${ticketId}/quote-document`),
      ]);
      setTicket(data.ticket);
      setHasDocument(doc.document !== null);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 404
          ? 'That quote does not exist.'
          : 'Could not load this quote.',
      );
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  // Moving between two quotes without remounting: clear the old one and
  // go back to the spinner, rather than showing the previous quote's
  // document under the new quote's heading.
  useEffect(() => {
    setTicket(null);
    setLoading(true);
  }, [ticketId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (hasDocument !== false || templates.length > 0) return;
    apiFetch<{ templates: QuoteTemplateSummary[] }>('/api/quote-documents/templates')
      .then((r) => setTemplates(r.templates.filter((t) => t.isActive)))
      .catch(() => setTemplates([]));
  }, [hasDocument, templates.length]);

  async function startFromTemplate(templateId: string): Promise<void> {
    if (!ticket) return;
    setApplying(templateId);
    setTemplateError(null);
    try {
      await apiFetch(`/api/quote-documents/${ticket.id}/template`, { method: 'POST', json: { templateId } });
      setPanelKey((k) => k + 1);
      await load();
    } catch (e) {
      setTemplateError(e instanceof ApiError ? e.message : 'Could not apply that template.');
    } finally {
      setApplying(null);
    }
  }

  if (loading && !ticket) {
    return (
      <Card>
        <p className="text-sm text-[var(--color-muted)]">Loading the quote…</p>
      </Card>
    );
  }

  // Only fatal when there is nothing to show. A refresh that fails after
  // a save must not replace a document the agent is still working on
  // with an error card — the save itself already succeeded or reported
  // its own failure.
  if (!ticket) {
    return (
      <Card>
        <p className="text-sm text-[var(--color-danger)]">{error ?? 'Quote not found.'}</p>
        <Link
          to="/admin/quotes"
          className="mt-3 inline-block text-sm font-semibold text-[var(--color-t3-blue)] hover:underline"
        >
          ← All quotes
        </Link>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/admin/quotes"
          className="text-sm text-[var(--color-muted)] hover:text-[var(--color-header)] hover:underline"
        >
          ← All quotes
        </Link>
        <h1 className="mt-1 text-2xl font-bold text-[var(--color-header)]">{ticket.subject}</h1>
        <p className="text-sm text-[var(--color-muted)]">
          {ticket.organizationName} ·{' '}
          <Link
            to={`/tickets/${ticket.id}`}
            className="text-[var(--color-t3-blue)] hover:underline"
          >
            T-{ticket.displayNumber}
          </Link>
        </p>
      </div>

      {error && (
        <p className="rounded-[var(--radius-button)] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {error} The quote on screen is still here — nothing has been lost.
        </p>
      )}

      {ticket.descriptionHtml.trim() && (
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">What the client asked for</h2>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            {ticket.requesterName ? `${ticket.requesterName}, ` : ''}from the client portal
            {typeof ticket.customFields.time_frame === 'string' && ticket.customFields.time_frame
              ? ` · Needed: ${ticket.customFields.time_frame}`
              : ''}
          </p>
          <div className="mt-3 text-sm text-[var(--color-body)]">
            <EmailBody html={ticket.descriptionHtml} />
          </div>
        </Card>
      )}

      {hasDocument === false && templates.length > 0 && (
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Start from a template</h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Copies the template's sections, pricing and agreements into this quote. Or skip this and scope it with
            Claude below.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                disabled={applying !== null}
                onClick={() => void startFromTemplate(t.id)}
                className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-3 text-left transition hover:border-[var(--color-t3-blue)] disabled:opacity-60"
              >
                <span className="block text-sm font-semibold text-[var(--color-header)]">
                  {applying === t.id ? 'Applying…' : t.name}
                </span>
                {t.description && (
                  <span className="mt-1 block text-xs text-[var(--color-muted)]">{t.description}</span>
                )}
              </button>
            ))}
          </div>
          {templateError && <p className="mt-2 text-sm text-[var(--color-danger)]">{templateError}</p>}
        </Card>
      )}

      {/* The chat sits ABOVE the builder because it is the way in. The
          builder is for correcting what comes out of it, not for
          producing a document from an empty form. */}
      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Scope it with Claude</h2>
        <p className="mt-1 mb-4 text-sm text-[var(--color-muted)]">
          Talk through the engagement. The scope of work, the hours by area and the delivery plan
          fill in below as we go; the rate and terms stay yours. Nothing is saved until you save it.
        </p>
        <QuoteSowChat
          organizationName={ticket.organizationName}
          quoteTitle={ticket.subject}
          currentDocument={currentSow}
          initialInput={hasDocument === false ? requestText(ticket) : undefined}
          onApply={(draft) => {
            setIncoming(draft);
            setIncomingSeq((n) => n + 1);
          }}
        />
      </Card>

      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-[var(--color-header)]">The quote</h2>
          <Button
            variant="outline"
            onClick={() => {
              void (async () => {
                // Reads the SAVED document, so the preview is what would
                // actually be sent. Unsaved edits are deliberately not
                // shown — a preview of something that does not exist yet
                // is the wrong thing to check a price against.
                const data = await apiFetch<{ document: QuoteDocument | null }>(
                  `/api/tickets/${ticket.id}/quote-document`,
                );
                setPreviewDoc(data.document);
                setPreviewPlanId(null);
                setPreviewing(true);
              })();
            }}
          >
            <Eye className="h-4 w-4" aria-hidden="true" />
            Preview
          </Button>
        </div>
        <QuoteDocumentPanel
          key={panelKey}
          ticketId={ticket.id}
          ticketSubject={ticket.subject}
          onSaved={load}
          incoming={incoming}
          incomingSeq={incomingSeq}
          onSectionsChange={setCurrentSow}
        />
      </Card>

      {previewing && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-label="Quote preview"
          onClick={() => setPreviewing(false)}
        >
          <div
            className="mx-auto w-full max-w-[1440px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-semibold uppercase tracking-[0.12em] text-white">
                As the client sees it
              </p>
              <button
                type="button"
                onClick={() => setPreviewing(false)}
                className="inline-flex items-center gap-1 rounded-[var(--radius-button)] bg-white/90 px-3 py-1.5 text-sm font-semibold text-[var(--color-header)] hover:bg-white"
              >
                <X className="h-4 w-4" aria-hidden="true" /> Close
              </button>
            </div>
            {/* No wrapper card: QuoteDocumentView brings its own, exactly
                as the client-facing page renders it. */}
            <div className="overflow-hidden rounded-[var(--radius-card)]">
              {previewDoc &&
                !previewDoc.organizationAddress &&
                documentUsesToken(previewDoc, CLIENT_ADDRESS_TOKEN) && (
                  <p className="bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    {previewDoc.organizationName} has no address on file, so the agreements still
                    read {CLIENT_ADDRESS_TOKEN}. Add it on the client's page - the quote cannot be
                    sent until it is.
                  </p>
                )}
              {previewDoc ? (
                <QuoteDocumentView
                  // Filled exactly as the send will fill it, so the preview
                  // shows the parties the client will actually read.
                  quote={{
                    title: previewDoc.title,
                    intro: previewDoc.intro,
                    closing: previewDoc.closing,
                    organizationName: previewDoc.organizationName,
                    preparedByAgentName: previewDoc.preparedByAgentName,
                    sections: previewDoc.sections.map((s) => ({
                      ...s,
                      blocks: fillClientBlocks(s.blocks, {
                        name: previewDoc.organizationName,
                        address: previewDoc.organizationAddress ?? null,
                      }),
                    })),
                    planOptions: previewDoc.planOptions ?? [],
                    deliveryPlan: previewDoc.deliveryPlan ?? [],
                    lineItems: previewDoc.lineItems.map((i) => ({
                      description: i.description,
                      quantity: i.quantity,
                      unit: i.unit,
                      unitPriceUsd: i.unitPriceUsd ?? 0,
                      amountUsd: i.amountUsd ?? 0,
                    })),
                    totalAmountUsd: previewDoc.totalAmountUsd ?? 0,
                    // Preview shows the payment terms the client will see,
                    // computed the same way the server charges them.
                    ...splitDeposit(
                      Math.round((previewDoc.totalAmountUsd ?? 0) * 100),
                      previewDoc.depositPercent,
                    ),
                  }}
                  // The agent can click through the plans to check how a
                  // choice reads. Local to the preview; nothing is saved.
                  selectedPlanId={previewPlanId}
                  onSelectPlan={setPreviewPlanId}
                />
              ) : (
                <div className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white p-8 text-center">
                  <p className="text-sm text-[var(--color-muted)]">
                    Nothing saved yet — save the quote and preview again.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
